import { Component, ElementRef, EventEmitter, Input, Output, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MockRule, MockStrategy, QueryFilter, ResponseTemplate } from './network.types';
import { NETWORK_METHODS, NetworkMonitorService } from './network-monitor.service';
import { JsonViewerComponent } from '../json-viewer/json-viewer.component';

@Component({
  selector: 'app-network-mock-row',
  standalone: true,
  imports: [FormsModule, MatIconModule, MatSlideToggleModule, JsonViewerComponent],
  host: {
    'draggable': 'true',
    '(dragstart)': 'onDragStart($event)',
    '(dragover)': 'onDragOver($event)',
    '(dragleave)': 'onDragLeave($event)',
    '(drop)': 'onDrop($event)',
    '(dragend)': 'onDragEnd()',
  },
  template: `
    <div class="row" [class.disabled]="!entry.enabled">
      <span class="cell toggle">
        <mat-slide-toggle
          [checked]="entry.enabled"
          (change)="onToggle($event.checked)"
          color="primary" />
      </span>

      @if (editingName) {
        <input class="cell name-input"
               [value]="entry.name"
               (keydown.enter)="commitName($any($event.target).value)"
               (keydown.escape)="editingName = false"
               (blur)="commitName($any($event.target).value)"
               #nameInput />
      } @else {
        <span class="cell name" [title]="entry.name" (dblclick)="startEditName()">{{ entry.name }}</span>
      }

      <select class="cell method-select" [class]="entry.method ? 'method-' + entry.method.toLowerCase() : ''"
              [value]="entry.method"
              (change)="onMethodChange($any($event.target).value)">
        <option value="">ANY</option>
        @for (m of methods; track m) {
          <option [value]="m" [selected]="m === entry.method">{{ m }}</option>
        }
      </select>

      <input
        class="cell path-input"
        [(ngModel)]="entry.path"
        (change)="persist()"
        placeholder="/path/* (wildcards allowed)"
      />

      @if (entry.hitCount) {
        <span class="cell hit-count" title="Hit count this session">{{ entry.hitCount }}×</span>
      } @else {
        <span class="cell hit-count"></span>
      }

      <span class="cell actions">
        <button class="icon-btn" (click)="toggleSection('filter')"
                [title]="expandedSection === 'filter' ? 'Collapse filter' : 'Edit request filter'"
                [class.active-section]="expandedSection === 'filter'"
                [class.has-config]="hasFilterConfig">
          <mat-icon>filter_alt</mat-icon>
        </button>
        <button class="icon-btn" (click)="toggleSection('query')"
                [title]="expandedSection === 'query' ? 'Collapse query' : 'Edit query filters'"
                [class.active-section]="expandedSection === 'query'"
                [class.has-config]="hasQueryConfig">
          <mat-icon>link</mat-icon>
        </button>
        <button class="icon-btn" (click)="toggleSection('response')"
                [title]="expandedSection === 'response' ? 'Collapse response' : 'Edit response'"
                [class.active-section]="expandedSection === 'response'">
          <mat-icon>data_object</mat-icon>
        </button>
        <button class="icon-btn" (click)="duplicate.emit(entry.id)" title="Duplicate">
          <mat-icon>content_copy</mat-icon>
        </button>
        <button class="icon-btn danger" [class.confirming]="confirmingDelete"
                (click)="onDeleteClick()" [title]="confirmingDelete ? 'Click again to confirm' : 'Delete mock'">
          @if (confirmingDelete) {
            <span class="confirm-text">Sure?</span>
          } @else {
            <mat-icon>delete_outline</mat-icon>
          }
        </button>
      </span>
    </div>

    @if (expandedSection === 'filter') {
      <div class="section-expanded">
        <div class="section-header">
          <span class="section-label">Request body filter</span>
        </div>
        <div class="fn-editor">
          <span class="fn-prefix">requestFilter(request) {{ '{' }}</span>
          <textarea
            class="fn-body"
            [value]="entry.requestFilterFn"
            (input)="onFilterFnChange($any($event.target).value)"
            (blur)="persist()"
            placeholder="  return true;"
            spellcheck="false"
            rows="3"
          ></textarea>
          <span class="fn-suffix">{{ '}' }}</span>
        </div>
      </div>
    }

    @if (expandedSection === 'query') {
      <div class="section-expanded">
        <div class="section-header">
          <span class="section-label">Query param filters</span>
          <button class="icon-btn" (click)="addQueryFilter()" title="Add filter">
            <mat-icon>add</mat-icon>
          </button>
        </div>
        @if (entry.queryFilters.length === 0) {
          <div class="empty-hint">No query filters — click + to add one</div>
        }
        @for (qf of entry.queryFilters; track $index) {
          <div class="query-row">
            <input class="query-input key"
                   [value]="qf.key"
                   (input)="qf.key = $any($event.target).value"
                   (blur)="persist()"
                   placeholder="param name" />
            <select class="query-comparator"
                    [value]="qf.comparator || 'eq'"
                    (change)="onComparatorChange(qf, $any($event.target).value)">
              <option value="eq">=</option>
              <option value="neq">≠</option>
              <option value="contains">contains</option>
              <option value="starts">starts</option>
              <option value="ends">ends</option>
              <option value="regex">regex</option>
              <option value="exists">exists</option>
            </select>
            @if ((qf.comparator || 'eq') !== 'exists') {
              <input class="query-input value"
                     [value]="qf.value"
                     (input)="qf.value = $any($event.target).value"
                     (blur)="persist()"
                     placeholder="value" />
            }
            <button class="icon-btn danger" (click)="removeQueryFilter($index)" title="Remove filter">
              <mat-icon>close</mat-icon>
            </button>
          </div>
        }
      </div>
    }

    @if (expandedSection === 'response') {
      <div class="section-expanded">
        <div class="strategy-tabs">
          <button class="strategy-tab" [class.active]="currentStrategy === 'mock'" (click)="setStrategy('mock')">
            <mat-icon>data_object</mat-icon> Mock
          </button>
          <button class="strategy-tab" [class.active]="currentStrategy === 'map'" (click)="setStrategy('map')">
            <mat-icon>functions</mat-icon> Map
          </button>
        </div>

        @if (currentStrategy === 'mock') {
          <div class="section-header">
            <span class="section-label">Mock response</span>
            <label class="status-label">
              Delay:
              <input class="delay-input" type="number"
                     [value]="entry.delay ?? 50"
                     (input)="onDelayChange($any($event.target).value)"
                     (blur)="persist()"
                     min="0" step="50" />
              <span class="unit">ms</span>
            </label>
            <label class="status-label">
              Status:
              <input class="status-input" type="number"
                     [value]="entry.mockStatus"
                     (input)="onStatusChange($any($event.target).value)"
                     (blur)="persist()"
                     min="100" max="599" />
            </label>
          </div>
          <div class="template-row">
            <span class="template-label">Template:</span>
            <select class="template-select" (change)="applyTemplate($any($event.target).value); $any($event.target).value = ''">
              <option value="">Select…</option>
              @for (t of monitor.allTemplates(); track t.id) {
                <option [value]="t.id">{{ t.name }} ({{ t.status }})</option>
              }
            </select>
            <button class="icon-btn" (click)="saveAsTemplate()" title="Save current as template">
              <mat-icon>bookmark_add</mat-icon>
            </button>
          </div>
          <app-json-viewer [data]="entry.mockResponse" mode="edit" [resizable]="true" [initialHeight]="160"
                           (save)="onResponseSave($event)" />
        }

        @if (currentStrategy === 'map') {
          <div class="section-header">
            <span class="section-label">Map real response</span>
          </div>
          <div class="map-hint">The real request will go through. Transform the response below.
            Call <code>setResponseStatusCode(code)</code> to override the status code.</div>
          <div class="fn-editor">
            <span class="fn-prefix">mapResponse(response, request) {{ '{' }}</span>
            <textarea
              class="fn-body"
              [value]="entry.mapFn || ''"
              (input)="onMapFnChange($any($event.target).value)"
              (blur)="persist()"
              placeholder="  return { ...response, modified: true };"
              spellcheck="false"
              rows="3"
            ></textarea>
            <span class="fn-suffix">{{ '}' }}</span>
          </div>
        }
      </div>
    }
  `,
  styles: [`
    :host {
      display: block;
      border-bottom: 1px solid #23233a;
    }

    .row {
      display: grid;
      grid-template-columns: 40px minmax(70px, 120px) 56px minmax(0, 1fr) 30px auto;
      align-items: center;
      gap: 4px;
      padding: 2px 6px;
      font-size: 0.72rem;
      color: #d4d4d8;
      min-height: 30px;
    }
    .row:hover { background: #23233a; }
    .row.disabled { opacity: 0.5; }

    .cell {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      min-width: 0;
    }

    .toggle { display: flex; align-items: center; }
    .toggle mat-slide-toggle { transform: scale(0.7); transform-origin: left center; }

    .name {
      font-weight: 600;
      color: #fbbf24;
      cursor: default;
    }
    .name-input {
      background: #0f0f1e;
      border: 1px solid #fbbf24;
      color: #fbbf24;
      font-weight: 600;
      font-family: inherit;
      font-size: inherit;
      padding: 1px 4px;
      border-radius: 3px;
      outline: none;
      min-width: 0;
    }

    .method-select {
      background: #0f0f1e;
      border: 1px solid #3a3a5a;
      border-radius: 3px;
      font-weight: 700;
      font-size: 0.66rem;
      font-family: ui-monospace, monospace;
      text-align: center;
      padding: 1px 2px;
      cursor: pointer;
      outline: none;
      min-width: 0;
      appearance: none;
      -webkit-appearance: none;
      color: #9ca3af;
    }
    .method-select:focus { border-color: #3b82f6; }
    .method-select option { background: #0f0f1e; color: #e5e7eb; }
    .method-get    { color: #3b82f6; }
    .method-post   { color: #16a34a; }
    .method-put    { color: #f97316; }
    .method-delete { color: #dc2626; }
    .method-patch  { color: #a855f7; }

    .path-input {
      background: #0f0f1e;
      border: 1px solid #3a3a5a;
      color: #e5e7eb;
      font-family: ui-monospace, monospace;
      font-size: 0.72rem;
      padding: 2px 6px;
      border-radius: 3px;
      outline: none;
      min-width: 0;
    }
    .path-input:focus { border-color: #3b82f6; }

    .actions {
      display: flex;
      gap: 1px;
      align-items: center;
      justify-content: flex-end;
    }

    .icon-btn {
      background: none;
      border: none;
      color: #9ca3af;
      padding: 2px;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: 3px;
      flex-shrink: 0;
    }
    .icon-btn mat-icon {
      font-size: 14px;
      width: 14px;
      height: 14px;
      line-height: 14px;
    }
    .icon-btn:hover:not(:disabled) { background: #374151; color: #fff; }
    .icon-btn:disabled { opacity: 0.25; cursor: not-allowed; }
    .icon-btn.danger:hover:not(:disabled) { background: #991b1b; color: #fff; }
    .icon-btn.active-section { color: #3b82f6; }
    .icon-btn.has-config {
      position: relative;
    }
    .icon-btn.has-config::after {
      content: '';
      position: absolute;
      top: 1px;
      right: 1px;
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background: #f97316;
    }

    /* Expanded sections */
    .section-expanded {
      background: #0a0a18;
      padding: 4px 6px 8px 6px;
    }
    .section-header {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 2px 4px;
    }
    .section-label {
      font-size: 0.66rem;
      font-weight: 600;
      color: #94a3b8;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      flex: 1;
      display: flex;
      align-items: center;
      gap: 6px;
    }

    /* Function editor */
    .fn-editor {
      background: #0f0f1e;
      border: 1px solid #3a3a5a;
      border-radius: 4px;
      padding: 6px 8px;
      margin: 4px;
      font-family: ui-monospace, monospace;
      font-size: 0.72rem;
    }
    .fn-prefix, .fn-suffix {
      color: #94a3b8;
      display: block;
      user-select: none;
    }
    .fn-body {
      display: block;
      width: 100%;
      background: transparent;
      border: none;
      color: #e5e7eb;
      font-family: ui-monospace, monospace;
      font-size: 0.72rem;
      resize: vertical;
      outline: none;
      padding: 2px 0 2px 12px;
      min-height: 40px;
    }

    /* Query filter rows */
    .query-row {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 2px 4px;
    }
    .query-input {
      background: #0f0f1e;
      border: 1px solid #3a3a5a;
      color: #e5e7eb;
      font-family: ui-monospace, monospace;
      font-size: 0.72rem;
      padding: 2px 6px;
      border-radius: 3px;
      outline: none;
    }
    .query-input:focus { border-color: #3b82f6; }
    .query-input.key { width: 120px; }
    .query-input.value { flex: 1; min-width: 0; }
    .query-comparator {
      background: #0f0f1e;
      border: 1px solid #3a3a5a;
      border-radius: 3px;
      color: #93c5fd;
      font-family: ui-monospace, monospace;
      font-size: 0.66rem;
      font-weight: 700;
      padding: 1px 2px;
      outline: none;
      cursor: pointer;
      min-width: 0;
      appearance: none;
      -webkit-appearance: none;
      text-align: center;
    }
    .query-comparator:focus { border-color: #3b82f6; }
    .query-comparator option { background: #0f0f1e; color: #e5e7eb; }
    .empty-hint {
      padding: 6px 8px;
      font-size: 0.68rem;
      color: #6b7280;
      font-style: italic;
    }

    /* Status input in response section */
    .status-label {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 0.66rem;
      font-weight: 600;
      color: #94a3b8;
      text-transform: uppercase;
    }
    .status-input {
      width: 52px;
      background: #0f0f1e;
      border: 1px solid #3a3a5a;
      color: #e5e7eb;
      font-family: ui-monospace, monospace;
      font-size: 0.72rem;
      padding: 1px 4px;
      border-radius: 3px;
      outline: none;
      text-align: center;
    }
    .status-input:focus { border-color: #3b82f6; }

    .section-expanded app-json-viewer {
      display: block;
    }

    /* Strategy tabs */
    .strategy-tabs {
      display: flex;
      border-bottom: 1px solid #23233a;
      margin-bottom: 4px;
    }
    .strategy-tab {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 5px 12px;
      background: none;
      border: none;
      border-bottom: 2px solid transparent;
      color: #6b7280;
      font-size: 0.7rem;
      font-weight: 600;
      cursor: pointer;
      transition: color 0.15s, border-color 0.15s;
    }
    .strategy-tab mat-icon {
      font-size: 14px;
      width: 14px;
      height: 14px;
    }
    .strategy-tab:hover { color: #d1d5db; }
    .strategy-tab.active {
      color: #3b82f6;
      border-bottom-color: #3b82f6;
    }

    .map-hint {
      font-size: 0.64rem;
      color: #6b7280;
      font-style: italic;
      padding: 2px 6px 4px;
    }
    .map-hint code {
      font-style: normal;
      font-family: ui-monospace, monospace;
      color: #93c5fd;
      background: #1e293b;
      padding: 1px 4px;
      border-radius: 3px;
      font-size: 0.62rem;
    }

    /* Hit counter */
    .hit-count {
      font-family: ui-monospace, monospace;
      font-size: 0.62rem;
      font-weight: 700;
      color: #94a3b8;
      text-align: center;
    }

    /* Delay input */
    .delay-input {
      width: 52px;
      background: #0f0f1e;
      border: 1px solid #3a3a5a;
      color: #e5e7eb;
      font-family: ui-monospace, monospace;
      font-size: 0.72rem;
      padding: 1px 4px;
      border-radius: 3px;
      outline: none;
      text-align: center;
    }
    .delay-input:focus { border-color: #3b82f6; }
    .unit {
      font-size: 0.6rem;
      color: #6b7280;
    }

    /* Template row */
    .template-row {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 2px 4px 4px;
    }
    .template-label {
      font-size: 0.62rem;
      font-weight: 600;
      color: #94a3b8;
      text-transform: uppercase;
    }
    .template-select {
      background: #0f0f1e;
      border: 1px solid #3a3a5a;
      border-radius: 3px;
      color: #e5e7eb;
      font-size: 0.66rem;
      padding: 2px 4px;
      outline: none;
      flex: 1;
      min-width: 0;
    }
    .template-select:focus { border-color: #3b82f6; }

    /* Inline delete confirmation */
    .icon-btn.confirming {
      background: #991b1b;
      color: #fff;
      animation: confirmPulse 0.6s ease-in-out infinite alternate;
    }
    @keyframes confirmPulse {
      from { opacity: 1; }
      to { opacity: 0.6; }
    }
    .confirm-text {
      font-size: 0.58rem;
      font-weight: 700;
      letter-spacing: 0.02em;
    }

    /* Drag & drop */
    :host { cursor: grab; }
    :host:active { cursor: grabbing; }
    :host.drag-over { border-top: 2px solid #3b82f6; }
  `],
})
export class NetworkMockRowComponent {
  @Input({ required: true }) entry!: MockRule;
  @Output() delete = new EventEmitter<string>();
  @Output() duplicate = new EventEmitter<string>();
  @Output() dragReorder = new EventEmitter<{ fromId: string; toId: string }>();

  readonly monitor = inject(NetworkMonitorService);
  private readonly el = inject(ElementRef);

  readonly methods = NETWORK_METHODS;
  expandedSection: 'filter' | 'query' | 'response' | null = null;
  editingName = false;

  get hasFilterConfig(): boolean {
    return !!this.entry.requestFilterFn?.trim();
  }

  get hasQueryConfig(): boolean {
    return this.entry.queryFilters.some(f => !!f.key.trim());
  }

  get currentStrategy(): MockStrategy {
    return this.entry.strategy || 'mock';
  }

  setStrategy(strategy: MockStrategy): void {
    this.entry.strategy = strategy;
    this.persist();
  }

  /** Inline delete confirmation (#61) */
  confirmingDelete = false;
  private deleteTimer: ReturnType<typeof setTimeout> | null = null;

  // ── Name ──────────────────────────────────────────────────────────────────

  startEditName(): void {
    this.editingName = true;
    setTimeout(() => {
      const inp = this.el.nativeElement.querySelector('.name-input') as HTMLInputElement;
      if (inp) { inp.focus(); inp.select(); }
    });
  }

  commitName(value: string): void {
    const trimmed = value.trim();
    if (trimmed && trimmed !== this.entry.name) {
      this.entry.name = trimmed;
      this.monitor.updateMock(this.entry.id, { name: trimmed });
    }
    this.editingName = false;
  }

  // ── Toggles ───────────────────────────────────────────────────────────────

  onToggle(enabled: boolean): void {
    this.entry.enabled = enabled;
    this.monitor.updateMock(this.entry.id, { enabled });
  }

  onMethodChange(method: string): void {
    this.entry.method = method;
    this.monitor.updateMock(this.entry.id, { method });
  }

  toggleSection(section: 'filter' | 'query' | 'response'): void {
    this.expandedSection = this.expandedSection === section ? null : section;
  }

  // ── Filter function ───────────────────────────────────────────────────────

  onFilterFnChange(value: string): void {
    this.entry.requestFilterFn = value;
  }

  // ── Query filters ─────────────────────────────────────────────────────────

  addQueryFilter(): void {
    this.entry.queryFilters = [...this.entry.queryFilters, { key: '', value: '' }];
    this.persist();
  }

  removeQueryFilter(index: number): void {
    this.entry.queryFilters = this.entry.queryFilters.filter((_, i) => i !== index);
    this.persist();
  }

  onComparatorChange(qf: QueryFilter, value: string): void {
    qf.comparator = value as QueryFilter['comparator'];
    this.persist();
  }

  // ── Response ──────────────────────────────────────────────────────────────

  onStatusChange(value: string): void {
    const num = parseInt(value, 10);
    if (!isNaN(num)) {
      this.entry.mockStatus = num;
    }
  }

  onResponseSave(payload: unknown): void {
    this.entry.mockResponse = payload;
    this.persist();
  }

  // ── Delay ──────────────────────────────────────────────────────────────────

  onDelayChange(value: string): void {
    const num = parseInt(value, 10);
    if (!isNaN(num) && num >= 0) {
      this.entry.delay = num;
    }
  }

  // ── Map function ──────────────────────────────────────────────────────────

  onMapFnChange(value: string): void {
    this.entry.mapFn = value;
  }

  // ── Templates ─────────────────────────────────────────────────────────────

  applyTemplate(templateId: string): void {
    if (!templateId) return;
    const template = this.monitor.allTemplates().find(t => t.id === templateId);
    if (!template) return;
    this.entry.mockStatus = template.status;
    this.entry.mockResponse = template.body;
    this.persist();
  }

  saveAsTemplate(): void {
    const name = prompt('Template name:');
    if (!name) return;
    this.monitor.addTemplate({
      name,
      status: this.entry.mockStatus,
      body: this.entry.mockResponse,
    });
  }

  // ── Inline delete ──────────────────────────────────────────────────────────

  onDeleteClick(): void {
    if (this.confirmingDelete) {
      if (this.deleteTimer) clearTimeout(this.deleteTimer);
      this.confirmingDelete = false;
      this.delete.emit(this.entry.id);
    } else {
      this.confirmingDelete = true;
      this.deleteTimer = setTimeout(() => this.confirmingDelete = false, 2000);
    }
  }

  // ── Drag & Drop ────────────────────────────────────────────────────────────

  onDragStart(event: DragEvent): void {
    // Prevent native drag ghost when dragging inside inputs or the json viewer.
    const elUnderCursor = document.elementFromPoint(event.clientX, event.clientY);
    if (elUnderCursor?.closest('input, textarea, select, .graph-container, .jv')) {
      event.preventDefault();
      return;
    }
    event.dataTransfer?.setData('text/plain', this.entry.id);
    event.dataTransfer!.effectAllowed = 'move';
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.dataTransfer!.dropEffect = 'move';
    this.el.nativeElement.classList.add('drag-over');
  }

  onDragLeave(_: DragEvent): void {
    this.el.nativeElement.classList.remove('drag-over');
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.el.nativeElement.classList.remove('drag-over');
    const fromId = event.dataTransfer?.getData('text/plain');
    if (fromId && fromId !== this.entry.id) {
      this.dragReorder.emit({ fromId, toId: this.entry.id });
    }
  }

  onDragEnd(): void {
    this.el.nativeElement.classList.remove('drag-over');
  }

  // ── Persistence ───────────────────────────────────────────────────────────

  persist(): void {
    this.monitor.updateMock(this.entry.id, {
      path: this.entry.path,
      requestFilterFn: this.entry.requestFilterFn,
      queryFilters: this.entry.queryFilters,
      mockResponse: this.entry.mockResponse,
      mockStatus: this.entry.mockStatus,
      delay: this.entry.delay,
      mapFn: this.entry.mapFn,
      strategy: this.entry.strategy,
    });
  }
}
